import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FieldError, Path, Resolver, useForm } from 'react-hook-form';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissions } from '@app/contexts/PermissionContext';
import { EmailDesignContent, MessageDesignContent, MessageTemplate } from '@app/models/messageTemplates';
import {
  createMessageTemplate,
  emailContentToTemplate,
  getMessageTemplate,
  messageContentToTemplate,
  templateToEmailContent,
  templateToMessageContent,
  updateMessageTemplate,
} from '@app/services/messageTemplates';
import { getApiErrorResponse } from '@app/services/api';
import { DEFAULT_EMAIL_DESIGN_CONTENT, DEFAULT_MESSAGE_DESIGN_CONTENT, MessageTemplateChannel } from '@app/utils/consts';
import { messageTemplateValidation } from '@app/utils/validations';

// `content` holds the editor shape of the page's channel (EmailDesignContent for EMAIL, MessageDesignContent for SMS)
export interface MessageTemplateFormValues {
  name: string;
  content: EmailDesignContent | MessageDesignContent;
}

type ValidationErrorResponse = { message?: string; errors?: Record<string, string> };

// Backend field name -> form path, per channel
const BACKEND_FIELD_PATHS: Record<MessageTemplateChannel, Record<string, Path<MessageTemplateFormValues>>> = {
  [MessageTemplateChannel.EMAIL]: {
    subject: 'content.subject',
    logo: 'content.logoUrl',
    heading: 'content.heading',
    message: 'content.message',
    buttonText: 'content.buttonText',
    buttonColor: 'content.buttonColor',
  },
  [MessageTemplateChannel.SMS]: {
    logo: 'content.logoUrl',
    heading: 'content.previewName',
    message: 'content.message',
  },
};

const getFormValues = (channel: MessageTemplateChannel, template?: MessageTemplate): MessageTemplateFormValues => {
  if (channel === MessageTemplateChannel.EMAIL) {
    return { name: template?.name ?? '', content: template ? templateToEmailContent(template) : DEFAULT_EMAIL_DESIGN_CONTENT };
  }

  return { name: template?.name ?? '', content: template ? templateToMessageContent(template) : DEFAULT_MESSAGE_DESIGN_CONTENT };
};

const getListUrl = (channel: MessageTemplateChannel) => (channel === MessageTemplateChannel.SMS ? `/templates?channel=${channel}` : '/templates');

export const useMessageTemplateEdit = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can('createCampaigns') || can('createAutomations');
  const isCreate = !id;

  const {
    data: template,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey: ['message-templates', 'item', id],
    queryFn: () => getMessageTemplate(id!).then((res) => res.data),
    enabled: Boolean(id),
    retry: (failureCount, error) => getApiErrorResponse(error).status !== 404 && failureCount < 2,
  });

  // Fixed for the page: from `?channel=` on create (email when missing), from the template on edit
  const channel = isCreate
    ? searchParams.get('channel') === MessageTemplateChannel.SMS
      ? MessageTemplateChannel.SMS
      : MessageTemplateChannel.EMAIL
    : template?.channel ?? MessageTemplateChannel.EMAIL;

  const form = useForm<MessageTemplateFormValues>({
    // The schema checks only the limited fields, so yup infers a looser shape than the form type
    resolver: messageTemplateValidation as unknown as Resolver<MessageTemplateFormValues>,
    defaultValues: getFormValues(channel),
  });
  const { isDirty } = form.formState;

  const isNotFound = getApiErrorResponse(loadError).status === 404;
  const usage = template?.usage;
  const usageCount = usage ? usage.automations.length + usage.campaigns.length : 0;

  // Load the template into the form once; later refetches must not overwrite unsaved edits
  const loadedTemplateId = useRef<string>();
  useEffect(() => {
    if (template && loadedTemplateId.current !== template.id) {
      loadedTemplateId.current = template.id;
      form.reset(getFormValues(template.channel, template));
    }
  }, [template, form]);

  // Set right before navigating away after a successful save, so the guard doesn't ask
  const skipLeaveGuard = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty && !skipLeaveGuard.current && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const saveMutation = useMutation({
    mutationFn: ({ name, content }: MessageTemplateFormValues) => {
      const templateContent = channel === MessageTemplateChannel.EMAIL ? emailContentToTemplate(content as EmailDesignContent) : messageContentToTemplate(content as MessageDesignContent);

      return id ? updateMessageTemplate(id, { name: name.trim(), ...templateContent }) : createMessageTemplate({ name: name.trim(), channel, ...templateContent });
    },
    onSuccess: () => {
      toast.success(isCreate ? 'Template created' : 'Template saved');
      queryClient.invalidateQueries({ queryKey: ['message-templates'] });
      skipLeaveGuard.current = true;
      navigate(getListUrl(channel));
    },
    onError: (error) => {
      const { data } = getApiErrorResponse<ValidationErrorResponse>(error);
      const fieldPaths = BACKEND_FIELD_PATHS[channel];

      Object.entries(data?.errors ?? {}).forEach(([field, reason]) => {
        const path: Path<MessageTemplateFormValues> | undefined = field === 'name' ? 'name' : fieldPaths[field];
        if (path) {
          form.setError(path, { message: reason });
        }
      });
      toast.error(data?.message || 'Failed to save template');
    },
  });

  // The design editors don't render validation errors, so they're listed next to the editor
  const contentErrors = Object.values(form.formState.errors.content ?? {})
    .map((fieldError) => (fieldError as FieldError | undefined)?.message)
    .filter((message): message is string => Boolean(message));

  const setContent = (value: EmailDesignContent | MessageDesignContent) => form.setValue('content', value, { shouldDirty: true, shouldValidate: form.formState.isSubmitted });

  const cancel = () => navigate(getListUrl(channel));

  return {
    form,
    isCreate,
    canEdit,
    template,
    isLoading: Boolean(id) && isLoading,
    isNotFound,
    loadError,
    usage,
    usageCount,
    channel,
    content: form.watch('content'),
    setContent,
    contentErrors,
    onSubmit: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    cancel,
    blocker,
  };
};
